import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { documentService } from '../../services/documentService';
import { getAuthToken } from '../../utils/authToken';
import { registerFileManagerOpen, runBackgroundDelete } from '../../utils/backgroundDelete';
import { useLanguage, tNodes } from '../../i18n';
import { deleteDialogCopy } from '../../utils/deleteDialogCopy';
import FileManagerToolbar from './FileManagerToolbar';
import FileManagerBreadcrumb from './FileManagerBreadcrumb';
import FileManagerGrid from './FileManagerGrid';
import FileManagerList from './FileManagerList';
import FileManagerContextMenu from './FileManagerContextMenu';
import CreateFolderModal from './CreateFolderModal';
import RecentlyDeletedModal from './RecentlyDeletedModal';
import MoveToFolderModal from './MoveToFolderModal';
import DocumentUpload from '../DocumentUpload';
import DocumentPreviewModal from '../DocumentPreviewModal';
import VersionHistoryDialog from './VersionHistoryDialog';
import { readRetentionPolicy, isImmediatePolicy } from '../../utils/retentionPolicy';
import { parseServerDate } from '../../utils/datetime';
import { userErrorMessage } from '../../utils/userError';
import Modal from '../ui/Modal';

// A comparable numeric timestamp for date sorting (Date order bug). Server
// timestamps arrive as RFC-1123 strings (Flask `jsonify` renders a datetime as
// e.g. "Tue, 30 Jun 2026 12:00:00 GMT"); comparing those as RAW strings sorts by
// weekday NAME first, then day-of-month as text — never chronology (the
// "Jun 30 → Jul 5 → Jul 4 → Jun 28" jumble). Parse to epoch millis and compare
// numerically instead. Unparseable / missing → 0 (sorts as oldest).
const dateValue = (iso) => { const d = parseServerDate(iso); return d ? d.getTime() : 0; };

// Persisted view/sort preferences (Default Settings #65) — the File Manager
// remembers the user's last view mode + sort across reloads / navigation.
const FM_PREFS = {
    viewMode: { key: 'omnis:fm-view-mode', fallback: 'grid', allowed: ['grid', 'list'] },
    sortBy: { key: 'omnis:fm-sort-by', fallback: 'name', allowed: ['name', 'date', 'size'] },
    sortOrder: { key: 'omnis:fm-sort-order', fallback: 'asc', allowed: ['asc', 'desc'] },
};
const readPref = ({ key, fallback, allowed }) => {
    try {
        const v = localStorage.getItem(key);
        return v && allowed.includes(v) ? v : fallback;
    } catch (_) { return fallback; }
};
const writePref = ({ key }, value) => {
    try { localStorage.setItem(key, value); } catch (_) { /* storage unavailable — non-fatal */ }
};

const FileManager = () => {
    const { t } = useLanguage();
    // Navigation state
    const [currentFolderId, setCurrentFolderId] = useState(null);
    const [breadcrumb, setBreadcrumb] = useState([]);

    // Content state
    const [folders, setFolders] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // View state — seeded from the user's persisted preferences (Default Settings #65).
    const [viewMode, setViewMode] = useState(() => readPref(FM_PREFS.viewMode)); // 'grid' or 'list'
    const [sortBy, setSortBy] = useState(() => readPref(FM_PREFS.sortBy));
    const [sortOrder, setSortOrder] = useState(() => readPref(FM_PREFS.sortOrder));

    // Persist view/sort changes so they survive a reload or navigation.
    useEffect(() => { writePref(FM_PREFS.viewMode, viewMode); }, [viewMode]);
    useEffect(() => { writePref(FM_PREFS.sortBy, sortBy); }, [sortBy]);
    useEffect(() => { writePref(FM_PREFS.sortOrder, sortOrder); }, [sortOrder]);

    // Selection state
    const [selectedFolders, setSelectedFolders] = useState(new Set());
    const [selectedDocuments, setSelectedDocuments] = useState(new Set());
    // Selection mode (New File Manager ticket): entered via the toolbar's
    // three-dot menu → "Select"; exiting clears any selection.
    const [selectionMode, setSelectionMode] = useState(false);
    const [lastSelectedItem, setLastSelectedItem] = useState(null); // { type, id, index }

    // Context menu state
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, type: null, item: null });

    // Modal state
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [showTrash, setShowTrash] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [renameItem, setRenameItem] = useState(null); // { type: 'folder'|'document', item: {...} }
    const [previewDocument, setPreviewDocument] = useState(null);
    const [versionHistoryDoc, setVersionHistoryDoc] = useState(null);
    // Move (#255 / #393): the SET being relocated — { documents: [...], folders: [...] }.
    const [moveSet, setMoveSet] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0 });

    // Drag state
    const [isDragging, setIsDragging] = useState(false);

    // "Delete and forget" (#79): tell the background runner this File Manager is
    // open, so it knows whether to fire a Bell notification when an async batch
    // delete finishes (only if the user has navigated away by then).
    useEffect(() => registerFileManagerOpen(), []);

    // Marquee/rubber-band selection state
    const contentRef = useRef(null);
    const [isMarqueeSelecting, setIsMarqueeSelecting] = useState(false);
    const [marqueeStart, setMarqueeStart] = useState({ x: 0, y: 0 });
    const [marqueeEnd, setMarqueeEnd] = useState({ x: 0, y: 0 });

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Load folder contents
    const loadContents = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await documentService.getFolderContents(
                debouncedSearch ? null : currentFolderId,
                debouncedSearch || null
            );
            setFolders(response.folders || []);
            setDocuments(response.documents || []);
            setBreadcrumb(response.breadcrumb || []);
        } catch (err) {
            console.error('Error loading folder contents:', err);
            setError(userErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    }, [currentFolderId, debouncedSearch]);

    useEffect(() => {
        loadContents();
    }, [loadContents]);

    // Refresh when a background upload finishes (file or folder). The
    // UploadManager fires `omnis:document-upload-complete` once each upload
    // settles, regardless of which folder it landed in — we always reload
    // current folder contents so newly-created folders / files appear.
    useEffect(() => {
        const handler = () => loadContents();
        window.addEventListener('omnis:document-upload-complete', handler);
        // Also refresh when a document is deleted via chat (delete_document /
        // discard_duplicate_upload) so it disappears from the file manager.
        window.addEventListener('omnis:document-deleted', handler);
        // ...and when PAI re-files a document via chat (move_document, #256) so the
        // Bunker reflects the move live — it leaves the folder you're looking at,
        // or appears in it, with no manual refresh.
        window.addEventListener('omnis:document-moved', handler);
        // ...and when a document is renamed elsewhere (the chat card's Manage
        // dialog, or PAI's rename_documents card) so the Bunker shows the new
        // name live (#330).
        window.addEventListener('omnis:document-renamed', handler);
        return () => {
            window.removeEventListener('omnis:document-upload-complete', handler);
            window.removeEventListener('omnis:document-deleted', handler);
            window.removeEventListener('omnis:document-moved', handler);
            window.removeEventListener('omnis:document-renamed', handler);
        };
    }, [loadContents]);

    // Drive the NON-blocking delete progress indicator + final refresh when a
    // background batch delete reports progress / completion (Delete and forget #79).
    useEffect(() => {
        const onProgress = (e) => {
            const { done = 0, total = 0 } = e.detail || {};
            setDeleteProgress({ current: done, total });
            setIsDeleting(total > 0 && done < total);
        };
        const onComplete = () => {
            setIsDeleting(false);
            setDeleteProgress({ current: 0, total: 0 });
            loadContents();
        };
        window.addEventListener('omnis:file-delete-progress', onProgress);
        window.addEventListener('omnis:file-delete-complete', onComplete);
        return () => {
            window.removeEventListener('omnis:file-delete-progress', onProgress);
            window.removeEventListener('omnis:file-delete-complete', onComplete);
        };
    }, [loadContents]);

    // Clear selection when changing folders
    useEffect(() => {
        setSelectedFolders(new Set());
        setSelectedDocuments(new Set());
        setLastSelectedItem(null);
    }, [currentFolderId]);

    // Navigation
    const navigateToFolder = useCallback((folderId) => {
        setSearchQuery('');
        setDebouncedSearch('');
        setCurrentFolderId(folderId);
    }, []);

    // "Back" navigation (File Manager Arrow ticket): go up one level to the parent
    // folder. The breadcrumb runs [My Documents, …ancestors…, current], so the
    // parent is the entry just before the last; its id is null when the current
    // folder sits directly under the root.
    const navigateBack = useCallback(() => {
        const parent = breadcrumb.length >= 2 ? breadcrumb[breadcrumb.length - 2] : null;
        navigateToFolder(parent ? parent.id : null);
    }, [breadcrumb, navigateToFolder]);
    // Show the back arrow only when inside an actual subfolder (not at root, not
    // while showing flat search results).
    const canGoBack = !!currentFolderId && !debouncedSearch;

    // Sort items
    const sortedFolders = useMemo(() => {
        return [...folders].sort((a, b) => {
            let cmp;
            if (sortBy === 'name') {
                const av = a.name || '', bv = b.name || '';
                cmp = av < bv ? -1 : av > bv ? 1 : 0;
            } else {
                // Folders have no size / original-upload date — sort by created_at
                // as a real timestamp (numeric, not string) so a Date sort mixes
                // folders in chronologically too.
                cmp = dateValue(a.created_at) - dateValue(b.created_at);
            }
            return sortOrder === 'asc' ? cmp : -cmp;
        });
    }, [folders, sortBy, sortOrder]);

    const sortedDocuments = useMemo(() => {
        return [...documents].sort((a, b) => {
            let cmp;
            if (sortBy === 'name') {
                const av = a.original_filename || '', bv = b.original_filename || '';
                cmp = av < bv ? -1 : av > bv ? 1 : 0;
            } else if (sortBy === 'size') {
                cmp = (a.file_size || 0) - (b.file_size || 0);
            } else {
                // 'date' — sort by the SAME value the Date column DISPLAYS (the
                // original upload date, falling back to created_at), compared as a
                // real timestamp. Using created_at while the column showed
                // original_uploaded_at — and comparing RFC-1123 strings — was the
                // Date order bug.
                cmp = dateValue(a.original_uploaded_at || a.created_at)
                    - dateValue(b.original_uploaded_at || b.created_at);
            }
            return sortOrder === 'asc' ? cmp : -cmp;
        });
    }, [documents, sortBy, sortOrder]);

    // Build a combined list of all items for range selection
    const allItems = useMemo(() => {
        const items = [];
        sortedFolders.forEach((folder, idx) => {
            items.push({ type: 'folder', id: folder.id, index: idx });
        });
        sortedDocuments.forEach((doc, idx) => {
            items.push({ type: 'document', id: doc.id, index: sortedFolders.length + idx });
        });
        return items;
    }, [sortedFolders, sortedDocuments]);

    // Selection handlers
    // Financial Folders (#145): protected /Financials folders are un-selectable so
    // they can never enter a batch delete/move. The Set is rebuilt whenever the
    // folder list changes.
    const protectedFolderIds = useMemo(
        () => new Set(folders.filter(f => f.is_protected).map(f => f.id)),
        [folders]
    );

    const handleSelect = useCallback((itemId, type, event) => {
        // In selection mode a plain click TOGGLES the item (like ctrl+click), so the
        // user can build a custom set of disparate items by ticking checkboxes —
        // without holding a modifier (Select Mission #64).
        const isCtrl = event?.ctrlKey || event?.metaKey || selectionMode;
        const isShift = event?.shiftKey;

        // Financial Folders (#145): a protected system folder can't be selected
        // (so it can never be batch-deleted/moved). Ignore the click entirely.
        if (type === 'folder' && protectedFolderIds.has(itemId)) return;

        // Find current item index in allItems
        const currentIndex = allItems.findIndex(item => item.id === itemId && item.type === type);

        // Handle shift+click range selection
        if (isShift && lastSelectedItem && currentIndex !== -1) {
            const lastIndex = allItems.findIndex(item => item.id === lastSelectedItem.id && item.type === lastSelectedItem.type);

            if (lastIndex !== -1) {
                const startIdx = Math.min(lastIndex, currentIndex);
                const endIdx = Math.max(lastIndex, currentIndex);

                const newSelectedFolders = new Set(isCtrl ? selectedFolders : []);
                const newSelectedDocuments = new Set(isCtrl ? selectedDocuments : []);

                for (let i = startIdx; i <= endIdx; i++) {
                    const item = allItems[i];
                    if (item.type === 'folder') {
                        if (protectedFolderIds.has(item.id)) continue; // #145: skip protected
                        newSelectedFolders.add(item.id);
                    } else {
                        newSelectedDocuments.add(item.id);
                    }
                }

                setSelectedFolders(newSelectedFolders);
                setSelectedDocuments(newSelectedDocuments);
                return;
            }
        }

        // Regular click or ctrl+click
        if (type === 'folder') {
            setSelectedFolders(prev => {
                const newSet = new Set(isCtrl ? prev : []);
                if (isCtrl && newSet.has(itemId)) {
                    newSet.delete(itemId);
                } else {
                    newSet.add(itemId);
                }
                return newSet;
            });
            if (!isCtrl) setSelectedDocuments(new Set());
        } else {
            setSelectedDocuments(prev => {
                const newSet = new Set(isCtrl ? prev : []);
                if (isCtrl && newSet.has(itemId)) {
                    newSet.delete(itemId);
                } else {
                    newSet.add(itemId);
                }
                return newSet;
            });
            if (!isCtrl) setSelectedFolders(new Set());
        }

        // Track last selected item for shift+click
        setLastSelectedItem({ type, id: itemId });
    }, [allItems, lastSelectedItem, selectedFolders, selectedDocuments, selectionMode, protectedFolderIds]);

    const handleSelectAll = useCallback(() => {
        // Financial Folders (#145): "Select All" never selects protected folders.
        setSelectedFolders(new Set(folders.filter(f => !f.is_protected).map(f => f.id)));
        setSelectedDocuments(new Set(documents.map(d => d.id)));
    }, [folders, documents]);

    const handleClearSelection = useCallback(() => {
        setSelectedFolders(new Set());
        setSelectedDocuments(new Set());
    }, []);

    // Exit selection mode (#392) — "completing ANY action on a selection
    // terminates multi-selection, clears all checkboxes, and returns the
    // interface to standard browsing".
    //
    // Clearing the selection SETS was never enough on its own: `selectionMode`
    // is what renders the checkbox overlay on every tile and turns a plain
    // click into a toggle (#64), so a delete/move/rename left the user still in
    // a mode they never asked to stay in, with empty checkboxes on every item
    // and single-click navigation silently disabled.
    //
    // Deliberately scoped to actions that COMPLETE and MUTATE: delete, move
    // (the #255 dialog and the drag-drop path) and rename. A CANCELLED action
    // leaves the selection alone — the user is still mid-task — and so does a
    // read-only action (download / preview / version history), which operates
    // on the right-clicked item, not on the selection.
    const exitSelection = useCallback(() => {
        setSelectionMode(false);
        handleClearSelection();
    }, [handleClearSelection]);

    // Double-click to open
    const handleOpen = useCallback((itemId, type) => {
        if (type === 'folder') {
            navigateToFolder(itemId);
        } else {
            const doc = documents.find(d => d.id === itemId);
            if (doc) {
                setPreviewDocument(doc);
            }
        }
    }, [documents, navigateToFolder]);

    // Context menu
    const handleContextMenu = useCallback((event, type, item) => {
        event.preventDefault();
        setContextMenu({
            visible: true,
            x: event.clientX,
            y: event.clientY,
            type,
            item
        });
    }, []);

    const closeContextMenu = useCallback(() => {
        setContextMenu({ visible: false, x: 0, y: 0, type: null, item: null });
    }, []);

    // Folder operations
    const handleCreateFolder = useCallback(async (name) => {
        try {
            await documentService.createFolder(name, currentFolderId);
            setShowCreateFolder(false);
            loadContents();
        } catch (err) {
            console.error('Error creating folder:', err);
            throw err;
        }
    }, [currentFolderId, loadContents]);

    const handleRename = useCallback(async (newName) => {
        if (!renameItem) return;

        try {
            if (renameItem.type === 'folder') {
                await documentService.renameFolder(renameItem.item.id, newName);
            } else {
                const resp = await documentService.renameDocument(renameItem.item.id, newName);
                // Tell the rest of the app the file has a new name (#330), so an
                // open chat's "Document created/updated" cards stop showing the
                // old one without waiting for their next conversation fetch.
                window.dispatchEvent(new CustomEvent('omnis:document-renamed', {
                    detail: { id: renameItem.item.id, name: resp?.original_filename || newName },
                }));
            }
            setRenameItem(null);
            exitSelection();   // #392 — a completed action ends multi-selection
            loadContents();
        } catch (err) {
            console.error('Error renaming:', err);
            throw err;
        }
    }, [renameItem, loadContents, exitSelection]);

    const handleDelete = useCallback(() => {
        const documentIds = Array.from(selectedDocuments);
        // Financial Folders (#145): protected /Financials folders are never
        // deletable — filter any out defensively (selection already excludes them).
        const folderIds = Array.from(selectedFolders).filter(id => !protectedFolderIds.has(id));
        if (documentIds.length + folderIds.length === 0) return;

        // FIRE-AND-FORGET (Delete and forget #79): kick the deletion off in a
        // module-level runner that survives this component, then immediately drop
        // the confirm + clear the selection so the user can navigate away / close
        // the File Manager. A NON-blocking progress strip tracks it while open;
        // per-file `omnis:document-deleted` events refresh the list as items go,
        // and if the user has left by the time it finishes, the runner posts a
        // Bell notification.
        setShowDeleteConfirm(false);
        exitSelection();   // #392 — not just the sets: the MODE ends here too
        // Recently Deleted (#126/#127): unless the retention policy is "Immediately",
        // deletes are SOFT — they go to the trash and can be restored. "Immediately"
        // keeps the historic hard delete.
        const mode = isImmediatePolicy(readRetentionPolicy()) ? 'permanent' : 'trash';
        runBackgroundDelete({ documentIds, folderIds, mode });
    }, [selectedFolders, selectedDocuments, exitSelection, protectedFolderIds]);

    // Drag and drop handlers
    const handleDragStart = useCallback((_e, _type, _item) => {
        setIsDragging(true);
    }, []);

    const handleDragEnd = useCallback((_e) => {
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback(async (targetFolderId, folderIds, documentIds) => {
        setIsDragging(false);

        // Filter out the target folder from the folders being moved
        const filteredFolderIds = folderIds.filter(id => id !== targetFolderId);

        if (filteredFolderIds.length === 0 && documentIds.length === 0) {
            return;
        }

        try {
            await documentService.moveItems(
                filteredFolderIds,
                documentIds,
                targetFolderId
            );
            exitSelection();   // #392 — a completed drag-move ends multi-selection
            loadContents();
        } catch (err) {
            console.error('Error moving items:', err);
        }
    }, [exitSelection, loadContents]);

    // Marquee selection handlers
    const handleMarqueeMouseDown = useCallback((e) => {
        // Only start marquee on left click and if clicking on empty space
        if (e.button !== 0) return;
        if (e.target !== contentRef.current && !e.target.classList.contains('marquee-area')) return;

        const rect = contentRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left + contentRef.current.scrollLeft;
        const y = e.clientY - rect.top + contentRef.current.scrollTop;

        setMarqueeStart({ x, y });
        setMarqueeEnd({ x, y });
        setIsMarqueeSelecting(true);

        // Clear selection unless ctrl is held
        if (!e.ctrlKey && !e.metaKey) {
            setSelectedFolders(new Set());
            setSelectedDocuments(new Set());
        }
    }, []);

    const handleMarqueeMouseMove = useCallback((e) => {
        if (!isMarqueeSelecting) return;

        const rect = contentRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left + contentRef.current.scrollLeft;
        const y = e.clientY - rect.top + contentRef.current.scrollTop;

        setMarqueeEnd({ x, y });

        // Calculate marquee rectangle
        const marqueeRect = {
            left: Math.min(marqueeStart.x, x),
            top: Math.min(marqueeStart.y, y),
            right: Math.max(marqueeStart.x, x),
            bottom: Math.max(marqueeStart.y, y)
        };

        // Find all items that intersect with the marquee
        const itemElements = contentRef.current.querySelectorAll('[data-item-id]');
        const newSelectedFolders = new Set(e.ctrlKey || e.metaKey ? selectedFolders : []);
        const newSelectedDocuments = new Set(e.ctrlKey || e.metaKey ? selectedDocuments : []);

        itemElements.forEach((el) => {
            const itemRect = el.getBoundingClientRect();
            const containerRect = contentRef.current.getBoundingClientRect();

            // Convert to container-relative coordinates
            const itemRelRect = {
                left: itemRect.left - containerRect.left + contentRef.current.scrollLeft,
                top: itemRect.top - containerRect.top + contentRef.current.scrollTop,
                right: itemRect.right - containerRect.left + contentRef.current.scrollLeft,
                bottom: itemRect.bottom - containerRect.top + contentRef.current.scrollTop
            };

            // Check intersection
            const intersects = !(
                itemRelRect.right < marqueeRect.left ||
                itemRelRect.left > marqueeRect.right ||
                itemRelRect.bottom < marqueeRect.top ||
                itemRelRect.top > marqueeRect.bottom
            );

            if (intersects) {
                const itemId = el.dataset.itemId;
                const itemType = el.dataset.itemType;
                if (itemType === 'folder') {
                    if (protectedFolderIds.has(itemId)) return; // #145: skip protected
                    newSelectedFolders.add(itemId);
                } else {
                    newSelectedDocuments.add(itemId);
                }
            }
        });

        setSelectedFolders(newSelectedFolders);
        setSelectedDocuments(newSelectedDocuments);
    }, [isMarqueeSelecting, marqueeStart, selectedFolders, selectedDocuments, protectedFolderIds]);

    const handleMarqueeMouseUp = useCallback(() => {
        setIsMarqueeSelecting(false);
    }, []);

    // Add global mouse up listener for marquee
    useEffect(() => {
        if (isMarqueeSelecting) {
            const handleGlobalMouseUp = () => setIsMarqueeSelecting(false);
            window.addEventListener('mouseup', handleGlobalMouseUp);
            return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
        }
    }, [isMarqueeSelecting]);

    // Calculate marquee rectangle for rendering
    const marqueeRect = useMemo(() => {
        if (!isMarqueeSelecting) return null;
        return {
            left: Math.min(marqueeStart.x, marqueeEnd.x),
            top: Math.min(marqueeStart.y, marqueeEnd.y),
            width: Math.abs(marqueeEnd.x - marqueeStart.x),
            height: Math.abs(marqueeEnd.y - marqueeStart.y)
        };
    }, [isMarqueeSelecting, marqueeStart, marqueeEnd]);

    // Upload complete handler
    const handleUploadComplete = useCallback(() => {
        setShowUpload(false);
        loadContents();
    }, [loadContents]);

    // Download document
    const handleDownload = useCallback(async (documentId, documentName) => {
        try {
            const token = getAuthToken();
            const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/documents/${documentId}/download`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = documentName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Error downloading:', err);
        }
    }, []);

    // Bulk Move (#393): the selected rows themselves (the dialog needs each
    // item's current folder to work out which destinations are no-ops).
    const selectedItemsForMove = useCallback(() => ({
        documents: documents.filter((d) => selectedDocuments.has(d.id)),
        folders: folders.filter((f) => selectedFolders.has(f.id)),
    }), [documents, folders, selectedDocuments, selectedFolders]);

    const hasSelection = selectedFolders.size > 0 || selectedDocuments.size > 0;
    const selectionCount = selectedFolders.size + selectedDocuments.size;
    // Drives the menu's "Select All ⇄ Deselect All" toggle (Select Mission #64).
    // Financial Folders (#145): protected folders are never selectable, so the
    // "all selected" comparison must exclude them or the toggle would never flip.
    const selectableItemCount = (folders.length - protectedFolderIds.size) + documents.length;
    const allSelected = selectableItemCount > 0 && selectionCount === selectableItemCount;

    return (
        <div className="flex flex-col h-full" onClick={closeContextMenu}>
            {/* Toolbar */}
            <FileManagerToolbar
                canGoBack={canGoBack}
                onBack={navigateBack}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                sortBy={sortBy}
                onSortByChange={setSortBy}
                sortOrder={sortOrder}
                onSortOrderChange={setSortOrder}
                onCreateFolder={() => setShowCreateFolder(true)}
                onUpload={() => setShowUpload(true)}
                onOpenTrash={() => setShowTrash(true)}
                hasSelection={hasSelection}
                selectionCount={selectionCount}
                allSelected={allSelected}
                onDelete={() => setShowDeleteConfirm(true)}
                onMove={() => setMoveSet(selectedItemsForMove())}
                onSelectAll={handleSelectAll}
                onClearSelection={handleClearSelection}
                selectionMode={selectionMode}
                onEnterSelectionMode={() => setSelectionMode(true)}
                onExitSelectionMode={exitSelection}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
            />

            {/* Breadcrumb */}
            <FileManagerBreadcrumb
                breadcrumb={breadcrumb}
                onNavigate={navigateToFolder}
                onDrop={handleDrop}
            />

            {/* Content area */}
            <div
                ref={contentRef}
                className="flex-1 overflow-auto px-6 py-4 relative marquee-area select-none"
                onMouseDown={handleMarqueeMouseDown}
                onMouseMove={handleMarqueeMouseMove}
                onMouseUp={handleMarqueeMouseUp}
            >
                {/* Marquee selection rectangle */}
                {marqueeRect && (
                    <div
                        className="absolute border border-white/60 bg-white/10 pointer-events-none z-50"
                        style={{
                            left: marqueeRect.left,
                            top: marqueeRect.top,
                            width: marqueeRect.width,
                            height: marqueeRect.height
                        }}
                    />
                )}

                {isLoading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin h-8 w-8 border-2 border-white/70 border-t-transparent rounded-full"></div>
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-64 text-pai-red">
                        <p>{error}</p>
                    </div>
                ) : folders.length === 0 && documents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-center marquee-area">
                        {debouncedSearch ? (
                            <>
                                <p className="text-[18px] font-bold text-white/70">{t('fmNoDocumentsFound', 'No documents found')}</p>
                                <p className="text-[14.5px] mt-1.5 text-white/40">{t('fmNoDocumentsMatching', 'No documents matching "{query}"', { query: debouncedSearch })}</p>
                            </>
                        ) : (
                            <>
                                <p className="text-[18px] font-bold text-white/70">{t('fmFolderEmpty', 'This folder is empty')}</p>
                                <p className="text-[14.5px] mt-1.5 text-white/40">{t('fmFolderEmptyHint', 'Create a folder or upload files to get started')}</p>
                            </>
                        )}
                    </div>
                ) : viewMode === 'grid' ? (
                    <FileManagerGrid
                        folders={sortedFolders}
                        documents={sortedDocuments}
                        selectedFolders={selectedFolders}
                        selectedDocuments={selectedDocuments}
                        selectionMode={selectionMode}
                        onSelect={handleSelect}
                        onOpen={handleOpen}
                        onContextMenu={handleContextMenu}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDrop={handleDrop}
                        isDragging={isDragging}
                    />
                ) : (
                    <FileManagerList
                        folders={sortedFolders}
                        documents={sortedDocuments}
                        selectedFolders={selectedFolders}
                        selectedDocuments={selectedDocuments}
                        selectionMode={selectionMode}
                        onSelect={handleSelect}
                        onOpen={handleOpen}
                        onContextMenu={handleContextMenu}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={(field) => {
                            if (sortBy === field) {
                                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                            } else {
                                setSortBy(field);
                                // First click on Date surfaces the most-recent docs
                                // first (descending) — the conventional file-manager
                                // behaviour the Date order ticket asks for; a second
                                // click toggles to ascending. Name/Size default asc.
                                setSortOrder(field === 'date' ? 'desc' : 'asc');
                            }
                        }}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDrop={handleDrop}
                        isDragging={isDragging}
                    />
                )}
            </div>

            {/* Context Menu */}
            {contextMenu.visible && (
                <FileManagerContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    type={contextMenu.type}
                    item={contextMenu.item}
                    hasSelection={hasSelection}
                    isProtected={contextMenu.type === 'folder' && !!contextMenu.item?.is_protected}
                    onClose={closeContextMenu}
                    onOpen={() => {
                        if (contextMenu.item) {
                            handleOpen(contextMenu.item.id, contextMenu.type);
                        }
                        closeContextMenu();
                    }}
                    onRename={() => {
                        setRenameItem({ type: contextMenu.type, item: contextMenu.item });
                        closeContextMenu();
                    }}
                    onDelete={() => {
                        if (contextMenu.item) {
                            const itemId = contextMenu.item.id;
                            const isInSelection = contextMenu.type === 'folder'
                                ? selectedFolders.has(itemId)
                                : selectedDocuments.has(itemId);

                            // If right-clicked item is already selected, delete all selected items
                            // Otherwise, select only the right-clicked item
                            if (!isInSelection) {
                                if (contextMenu.type === 'folder') {
                                    setSelectedFolders(new Set([itemId]));
                                    setSelectedDocuments(new Set());
                                } else {
                                    setSelectedFolders(new Set());
                                    setSelectedDocuments(new Set([itemId]));
                                }
                            }
                            // If already in selection, keep the existing selection
                        }
                        setShowDeleteConfirm(true);
                        closeContextMenu();
                    }}
                    onDownload={() => {
                        if (contextMenu.type === 'document' && contextMenu.item) {
                            handleDownload(contextMenu.item.id, contextMenu.item.original_filename);
                        }
                        closeContextMenu();
                    }}
                    onNewFolder={() => {
                        setShowCreateFolder(true);
                        closeContextMenu();
                    }}
                    onVersionHistory={() => {
                        if (contextMenu.type === 'document' && contextMenu.item) {
                            setVersionHistoryDoc(contextMenu.item);
                        }
                        closeContextMenu();
                    }}
                    onMove={() => {
                        // Move (#255 / #393): on an item that is PART of the current
                        // selection the whole selection moves (the same rule Delete
                        // uses); on an item outside it, just that item.
                        const item = contextMenu.item;
                        if (item) {
                            const inSelection = contextMenu.type === 'folder'
                                ? selectedFolders.has(item.id)
                                : selectedDocuments.has(item.id);
                            if (inSelection) {
                                setMoveSet(selectedItemsForMove());
                            } else if (contextMenu.type === 'folder') {
                                setMoveSet({ documents: [], folders: [item] });
                            } else {
                                setMoveSet({ documents: [item], folders: [] });
                            }
                        }
                        closeContextMenu();
                    }}
                />
            )}

            {/* Create Folder Modal */}
            {showCreateFolder && (
                <CreateFolderModal
                    onClose={() => setShowCreateFolder(false)}
                    onCreate={handleCreateFolder}
                />
            )}

            {/* Move to folder (#255) — "Select & Activate" destination picker.
                onMoved reloads the list in place, so the move is reflected
                reactively with zero page refresh. #392: a COMPLETED move also
                ends multi-selection; `onClose` (Cancel / Esc / backdrop) does
                not, so an abandoned move leaves the user's ticks intact. */}
            {moveSet && (
                <MoveToFolderModal
                    documents={moveSet.documents}
                    folders={moveSet.folders}
                    onClose={() => setMoveSet(null)}
                    onMoved={() => { exitSelection(); loadContents(); }}
                />
            )}

            {/* Recently Deleted (trash) view (#126/#127) */}
            {showTrash && (
                <RecentlyDeletedModal
                    onClose={() => setShowTrash(false)}
                    onChanged={loadContents}
                />
            )}

            {/* Rename Modal */}
            {renameItem && (
                <CreateFolderModal
                    onClose={() => setRenameItem(null)}
                    onCreate={handleRename}
                    initialName={renameItem.type === 'folder' ? renameItem.item.name : renameItem.item.original_filename}
                    title={renameItem.type === 'folder'
                        ? t('fmRenameFolderTitle', 'Rename Folder')
                        : t('fmRenameDocumentTitle', 'Rename Document')}
                    submitLabel={t('commonRename', 'Rename')}
                />
            )}

            {/* Upload Modal — also the staging table (#395): the picked files are
                listed with a destination each. The shell is a flex column capped
                at 85vh so the body scrolls INSIDE it and the Start button (the
                DocumentUpload footer) always stays reachable, at any height. */}
            {showUpload && (
                <Modal onClose={() => setShowUpload(false)} size="xl" layout="column" closeOnBackdrop={false} cardClassName="max-h-[85dvh] overflow-hidden">
                        <div className="px-6 py-4 border-b border-pai-hairline flex justify-between items-center flex-shrink-0">
                            <h3 className="text-[19px] font-bold text-white">{t('fmUploadFiles', 'Upload Files')}</h3>
                            <button
                                onClick={() => setShowUpload(false)}
                                className="pai-icon-btn w-[38px] h-[38px]"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6 flex-1 min-h-0 flex flex-col">
                            <DocumentUpload
                                onUploadComplete={handleUploadComplete}
                                onCancel={() => setShowUpload(false)}
                                folderId={currentFolderId}
                                currentFolderLabel={breadcrumb.slice(1).map((b) => b.name).join(' / ') || '…'}
                            />
                        </div>
                    </Modal>
            )}

            {/* Delete Confirmation */}
            {showDeleteConfirm && (
                <Modal onClose={() => setShowDeleteConfirm(false)} closeOnBackdrop={false} scrimTestId="fm-delete-dialog" cardClassName="p-7 min-w-0">
                        {(() => {
                            // #473 — the dialog names what is about to be deleted.
                            const copy = deleteDialogCopy({
                                documents: documents.filter((d) => selectedDocuments.has(d.id)),
                                folders: folders.filter((f) => selectedFolders.has(f.id)),
                                immediate: isImmediatePolicy(readRetentionPolicy()),
                                t,
                            });
                            const nodes = copy.kind === 'multi'
                                ? t(copy.titleKey, copy.titleFallback, { n: copy.count, s: copy.count !== 1 ? 's' : '' })
                                : tNodes(t, copy.titleKey, copy.titleFallback, {
                                    name: <span data-testid="fm-delete-target" title={copy.name} className="break-words max-w-full">{copy.name}</span>,
                                });
                            return (
                                <>
                                    <h3 className="text-[19px] font-bold text-white mb-3 break-words min-w-0" data-testid="fm-delete-title">{nodes}</h3>
                                    {copy.detail && <p className="text-[13px] text-pai-secondary mb-2">{copy.detail}</p>}
                                    <p className="text-[14.5px] leading-relaxed text-pai-secondary mb-6" data-testid="fm-delete-body">{copy.body}</p>
                                </>
                            );
                        })()}
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="pai-btn-ghost"
                            >
                                {t('commonCancel', 'Cancel')}
                            </button>
                            <button
                                onClick={handleDelete}
                                className="pai-btn-danger"
                            >
                                {t('commonDelete', 'Delete')}
                            </button>
                        </div>
                    </Modal>
            )}

            {/* Delete Progress — NON-blocking corner toast (Delete and forget #79).
                No dark backdrop, no inset-0: the user can keep browsing or close the
                File Manager while the background runner finishes (and the Bell
                notifies them if they leave). */}
            {isDeleting && deleteProgress.total > 0 && (
                <div data-testid="fm-delete-toast" className="fixed bottom-4 right-4 z-50 w-72 bg-pai-modal border border-pai rounded-card shadow-modal-sm p-4 pointer-events-none">
                    <div className="flex justify-between text-[14px] font-semibold text-white mb-2.5">
                        <span>{t('fmDeletingFiles', 'Deleting files…')}</span>
                        <span className="tnum">{deleteProgress.current} / {deleteProgress.total}</span>
                    </div>
                    <div className="w-full bg-white/[0.09] rounded-full h-1.5 overflow-hidden">
                        <div
                            className="bg-pai-red h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${(deleteProgress.current / deleteProgress.total) * 100}%` }}
                        ></div>
                    </div>
                    <p className="mt-2.5 text-[12px] text-pai-meta">
                        {t('fmDeletingFilesHint', "You can keep working — we'll notify you when it's done.")}
                    </p>
                </div>
            )}

            {/* Version History */}
            {versionHistoryDoc && (
                <VersionHistoryDialog
                    documentId={versionHistoryDoc.id}
                    documentName={versionHistoryDoc.original_filename}
                    onClose={() => setVersionHistoryDoc(null)}
                    onPreview={(versionId, name) => {
                        setVersionHistoryDoc(null);
                        setPreviewDocument({ id: versionId, original_filename: name });
                    }}
                />
            )}

            {/* Document Preview */}
            {previewDocument && (
                <DocumentPreviewModal
                    documentId={previewDocument.id}
                    documentName={previewDocument.original_filename}
                    onClose={() => setPreviewDocument(null)}
                />
            )}
        </div>
    );
};

export default FileManager;
